import type { ListBuilderConfiguration } from '../types';

/**
 * Conversation Tool list configuration
 * Shows AI agent tools available in the system
 * Converted from JSON to TypeScript for type safety
 */
export const CONVERSATION_TOOL_CONFIG: ListBuilderConfiguration = {
	lists: [
		{
			id: 'default-tools-all',
			cacheTTL: 600000,
			name: 'All Tools',
			type: 'default',
			active: true,
			columns: [
				{
					id: 'tool-name',
					fields: [
						{
							id: 'name-field',
							dataConfig: {
								type: 'field',
								field: { fieldName: 'name', objectTable: '_conversation_tool' },
							},
							styleConfig: {
								size: 'md',
								colorRule: { type: 'default' },
							},
							displayStyle: 'text',
						},
					],
					headers: [{ id: 'h1', text: 'Name' }],
					sortable: true,
					alignment: 'left',
					sortFields: [
						{
							direction: 'ASC',
							fieldName: 'name',
							objectTable: '_conversation_tool',
						},
					],
					verticalAlignment: 'middle',
				},
				{
					id: 'tool-type',
					fields: [
						{
							id: 'type-field',
							dataConfig: {
								type: 'field',
								field: { fieldName: 'type', objectTable: '_conversation_tool' },
							},
							styleConfig: {
								size: 'sm',
								colorRule: { type: 'default' },
							},
							displayStyle: 'badge',
						},
					],
					headers: [{ id: 'h2', text: 'Type' }],
					sortable: true,
					alignment: 'center',
					sortFields: [
						{
							direction: 'ASC',
							fieldName: 'type',
							objectTable: '_conversation_tool',
						},
					],
					verticalAlignment: 'middle',
				},
				{
					id: 'tool-is-default',
					fields: [
						{
							id: 'is-default-field',
							dataConfig: {
								type: 'field',
								field: {
									fieldName: 'isDefault',
									objectTable: '_conversation_tool',
								},
							},
							styleConfig: {
								size: 'sm',
								colorRule: {
									type: 'value-mapping',
									valueMapping: [
										{
											value: 'true',
											color: { bg: '#3b82f6', text: '#ffffff' },
										},
										{
											value: 'false',
											color: { bg: '#6b7280', text: '#ffffff' },
										},
									],
								},
							},
							displayStyle: 'badge',
						},
					],
					headers: [{ id: 'h-is-default', text: 'Source' }],
					sortable: true,
					alignment: 'center',
					sortFields: [
						{
							direction: 'DESC',
							fieldName: 'isDefault',
							objectTable: '_conversation_tool',
						},
					],
					verticalAlignment: 'middle',
				},
				{
					id: 'tool-description',
					fields: [
						{
							id: 'description-field',
							dataConfig: {
								type: 'field',
								field: {
									fieldName: 'humanReadableDescription',
									objectTable: '_conversation_tool',
								},
							},
							styleConfig: {
								size: 'sm',
								colorRule: { type: 'default' },
								textExpansion: { enabled: false },
							},
							displayStyle: 'text',
						},
					],
					headers: [{ id: 'h3', text: 'Description' }],
					sortable: false,
					alignment: 'left',
					verticalAlignment: 'middle',
				},
				{
					id: 'created-date',
					fields: [
						{
							id: 'created-date-field',
							dataConfig: {
								type: 'field',
								field: {
									fieldName: 'createdDate',
									objectTable: '_conversation_tool',
								},
							},
							styleConfig: {
								size: 'sm',
								colorRule: { type: 'default' },
								formatOptions: { dateFormat: 'date-medium' },
							},
							displayStyle: 'date',
						},
					],
					headers: [{ id: 'h4', text: 'Created' }],
					sortable: true,
					alignment: 'center',
					sortFields: [
						{
							direction: 'DESC',
							fieldName: 'createdDate',
							objectTable: '_conversation_tool',
						},
					],
					verticalAlignment: 'middle',
				},
				{
					id: 'actions',
					fields: [
						{
							id: 'actions-dropdown',
							dataConfig: {
								type: 'custom_text',
								customText: '',
							},
							styleConfig: {
								size: 'md',
								colorRule: { type: 'default' },
							},
							displayStyle: 'actions_dropdown',
							dropdownActions: [
								{
									id: 'copy-action',
									label: 'Copy',
									iconName: 'copy',
									callbackName: 'copyTool',
									variant: 'primary',
									visibility: 'visible',
								},
								{
									id: 'edit-action',
									label: 'Edit',
									iconName: 'edit',
									callbackName: 'editTool',
									variant: 'primary',
									visibility: 'visible',
								},
							],
						},
					],
					headers: [{ id: 'h5', text: '' }],
					width: 80,
					align: 'center',
					sortable: false,
				},
			],
			description: 'All AI agent tools available in the system',
			defaultSortId: 'column-tool-name-asc',
			sortOrder: 0,
		},
	],
	paging: {
		defaultPageSize: 25,
		pageSizeOptions: [25, 50, 100],
	},
	filters: {
		activeFilters: ['toolSearch', 'toolType'],
		defaultFilters: {},
	},
	objects: [
		{
			baseUrl: '/ai-agents/tools/:id',
			objectLabel: 'Tool',
			objectTable: '_conversation_tool',
			selectedFields: [
				{ fieldName: 'id', fieldLabel: 'ID' },
				{ fieldName: 'name', fieldLabel: 'Name' },
				{ fieldName: 'type', fieldLabel: 'Type' },
				{ fieldName: 'isDefault', fieldLabel: 'Source' },
				{ fieldName: 'description', fieldLabel: 'Description' },
				{
					fieldName: 'humanReadableDescription',
					fieldLabel: 'Short Description',
				},
				{ fieldName: 'createdDate', fieldLabel: 'Created Date' },
				{ fieldName: 'modifiedDate', fieldLabel: 'Modified Date' },
				{ fieldName: 'metadata', fieldLabel: 'Configuration' },
			],
		},
	],
	tableSorts: [
		{
			id: 'name',
			name: 'Name',
			fields: [
				{
					direction: 'ASC',
					fieldName: 'name',
					objectTable: '_conversation_tool',
				},
			],
			description: 'Sort by tool name',
		},
		{
			id: 'type',
			name: 'Type',
			fields: [
				{
					direction: 'ASC',
					fieldName: 'type',
					objectTable: '_conversation_tool',
				},
			],
			description: 'Sort by tool type',
		},
		{
			id: 'created',
			name: 'Created Date',
			fields: [
				{
					direction: 'DESC',
					fieldName: 'createdDate',
					objectTable: '_conversation_tool',
				},
			],
			description: 'Sort by creation date',
		},
	],
};
